// packages/kern/src/silben.ts — deutsche Silbentrennung (T45). KEIN DOM.
//
// Außer `text-canvas.ts` darf keine Datei in `packages/kern/src` `document`,
// `window`, `navigator`, `fetch`, `localStorage` oder einen React-Import
// enthalten.
//
// Liangs Verfahren (das von TeX) mit den Mustern der reformierten deutschen
// Rechtschreibung aus `silben-de.ts`. Gebraucht wird es nur, wo ein Wort in
// Mindestschrift breiter ist als sein Kasten (`wrapSilben()` in text.ts) —
// sonst stand „Datenübergabe“ links und rechts über den Kasten hinaus.

import { MUSTER_DE } from "./silben-de";

let tabelle: Map<string, number[]> | null = null;
let laengstes = 0;

/** Die Muster einmal beim ersten Gebrauch zerlegen: Buchstaben -> Ziffernfolge. */
function muster(): Map<string, number[]> {
  if (tabelle) return tabelle;
  const t = new Map<string, number[]>();
  for (const roh of MUSTER_DE.split(" ")) {
    if (!roh) continue;
    const buchstaben = roh.replace(/[0-9]/g, "");
    const ziffern = new Array<number>(buchstaben.length + 1).fill(0);
    let i = 0;
    for (const z of roh) {
      if (z >= "0" && z <= "9") ziffern[i] = Number(z);
      else i += z.length;
    }
    t.set(buchstaben, ziffern);
    if (buchstaben.length > laengstes) laengstes = buchstaben.length;
  }
  tabelle = t;
  return t;
}

/**
 * Trennstellen in einem reinen Buchstabenlauf: Stelle `q` heißt „vor
 * `lauf[q]` darf getrennt werden“. Links und rechts bleiben mindestens zwei
 * Buchstaben (wie TeX für Deutsch). Ändert `toLowerCase()` die Länge — das
 * gibt es bei deutschen Wörtern nicht —, wird nicht getrennt statt falsch.
 */
function liang(lauf: string): number[] {
  const n = lauf.length;
  if (n < 4) return [];
  const t = muster();
  const w = "." + lauf.toLowerCase() + ".";
  if (w.length !== n + 2) return [];
  const p = new Array<number>(w.length + 1).fill(0);
  for (let i = 0; i < w.length; i++) {
    const bis = Math.min(w.length, i + laengstes);
    for (let j = i + 1; j <= bis; j++) {
      const z = t.get(w.slice(i, j));
      if (!z) continue;
      for (let k = 0; k < z.length; k++) if (z[k]! > p[i + k]!) p[i + k] = z[k]!;
    }
  }
  const aus: number[] = [];
  for (let q = 2; q <= n - 2; q++) if (p[q + 1]! % 2 === 1) aus.push(q);
  return aus;
}

export interface Trennstelle {
  /** Getrennt wird VOR `wort[pos]`. */
  pos: number;
  /** `true`: am Zeilenende kommt ein Trennstrich dazu. `false`: dort steht
   *  schon ein Bindestrich oder Schrägstrich, der bleibt allein stehen. */
  strich: boolean;
}

/**
 * Alle erlaubten Trennstellen eines Wortes, aufsteigend. Buchstabenläufe
 * werden einzeln getrennt („BVV-Ausschuss“ -> „Aus-schuss“), und nach einem
 * vorhandenen Binde- oder Schrägstrich darf immer umbrochen werden.
 */
export function trennstellen(wort: string): Trennstelle[] {
  const aus: Trennstelle[] = [];
  const lauf = /\p{L}+/gu;
  for (let t = lauf.exec(wort); t; t = lauf.exec(wort)) {
    for (const q of liang(t[0])) aus.push({ pos: t.index + q, strich: true });
  }
  for (let i = 1; i < wort.length - 1; i++) {
    const z = wort[i];
    if ((z === "-" || z === "/" || z === "–") && /[\p{L}\d]/u.test(wort[i + 1]!)) {
      aus.push({ pos: i + 1, strich: false });
    }
  }
  return aus.sort((a, b) => a.pos - b.pos);
}
